import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Check, Mail } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { submitDirectoryListing } from "../services/directoryService";
import { nextPhoneValue } from "../utils/phone";
import { hasPremiumFeatureAccess } from "../utils/subscriptionAccess";

const PA_MAX = 3;
const PA_OPTIONS = [
  "Civil Litigation",
  "Business / Corporate / Contracts",
  "Labor & Employment",
  "Criminal Defense / Post-Conviction",
  "Personal Injury / Insurance",
  "Family Law",
  "Estate Planning / Probate",
  "Immigration",
  "Real Estate / Construction",
  "Bankruptcy / Debt Relief",
  "Other",
];

/** Matches the server cap in backend/routes/directory.js. */
const PA_OTHER_MAX = 35;

/** Validated fields in the order they appear on the form, top to bottom. */
const VALIDATED_FIELD_ORDER = [
  "firstName",
  "lastName",
  "email",
  "publicEmail",
  "phone",
  "state",
  "website",
  "role",
  "consent",
];

export default function DirectoryApplyForm({ open, onClose }) {
  const { user, isAuthenticated } = useAuth();
  const isLoggedIn = !!isAuthenticated;
  const { darkMode } = useTheme();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [publicEmail, setPublicEmail] = useState("");
  const [useVerificationEmail, setUseVerificationEmail] = useState(false);
  const [phone, setPhone] = useState("");
  const [publishPhone, setPublishPhone] = useState(false);
  const [city, setCity] = useState("");
  const [stateVal, setStateVal] = useState("");
  const [firmName, setFirmName] = useState("");
  const [yearsInPractice, setYearsInPractice] = useState("");
  const [role, setRole] = useState("");
  const [freeConsultation, setFreeConsultation] = useState(null);
  const [practiceAreas, setPracticeAreas] = useState(new Set());
  const [otherArea, setOtherArea] = useState("");
  const [website, setWebsite] = useState("");
  const [publicList, setPublicList] = useState(false);

  const [firstNameError, setFirstNameError] = useState("");
  const [lastNameError, setLastNameError] = useState("");
  const [emailErrorMsg, setEmailErrorMsg] = useState("");
  const [publicEmailError, setPublicEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [websiteError, setWebsiteError] = useState("");
  const [stateError, setStateError] = useState("");
  const [roleError, setRoleError] = useState("");
  const [consentError, setConsentError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [verificationSent, setVerificationSent] = useState(false);
  const [formError, setFormError] = useState("");
  // Server-supplied code for the duplicate-submission cases, so an attorney
  // who already has an approved listing gets a way in rather than a dead end.
  const [formErrorCode, setFormErrorCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const successRef = useRef(null);

  // The submit button sits at the bottom of a long form, so a failed submit
  // has to bring the first problem into view — otherwise the red text above
  // the fold goes unnoticed. A fresh object per submit re-runs the effect
  // even when the same field fails twice in a row.
  const [invalidFocus, setInvalidFocus] = useState(null);
  const firstNameRef = useRef(null);
  const lastNameRef = useRef(null);
  const emailRef = useRef(null);
  const publicEmailRef = useRef(null);
  const phoneRef = useRef(null);
  const stateRef = useRef(null);
  const websiteRef = useRef(null);
  const roleRef = useRef(null);
  const consentRef = useRef(null);

  useEffect(() => {
    if (!invalidFocus) return;
    const el = {
      firstName: firstNameRef,
      lastName: lastNameRef,
      email: emailRef,
      publicEmail: publicEmailRef,
      phone: phoneRef,
      state: stateRef,
      website: websiteRef,
      role: roleRef,
      consent: consentRef,
    }[invalidFocus.field]?.current;
    if (!el) return;

    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    el.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "center" });
    // preventScroll so the focus jump does not cut the smooth scroll short.
    const focusTarget = el.tagName === "INPUT" ? el : el.querySelector("input");
    focusTarget?.focus({ preventScroll: true });

    if (reduceMotion) return;
    // The role group is a row of pills; pulse each pill rather than the
    // square box around them.
    const targets = el.dataset.pulseChildren ? Array.from(el.children) : [el];
    targets.forEach((target) =>
      target.animate?.(
        [
          { boxShadow: "0 0 0 0 rgba(239, 68, 68, 0.55)" },
          { boxShadow: "0 0 0 6px rgba(239, 68, 68, 0)" },
        ],
        { duration: 700, iterations: 2, easing: "ease-out" },
      ),
    );
  }, [invalidFocus]);

  // The confirmation replaces the form in place, so on a long page it can land
  // mid-viewport or below the fold, leaving submitters unsure whether anything
  // happened. Bring it into view as soon as it renders.
  useEffect(() => {
    if (!successMsg || !successRef.current) return;
    successRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [successMsg]);

  const handlePAChange = (area, checked) => {
    setPracticeAreas((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(area);
      } else {
        next.delete(area);
      }
      if (!next.has("Other")) setOtherArea("");
      return next;
    });
  };

  const handleUseVerificationEmailChange = (checked) => {
    setUseVerificationEmail(checked);
    if (checked) {
      setPublicEmail("");
      setPublicEmailError("");
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setFirstNameError("");
    setLastNameError("");
    setEmailErrorMsg("");
    setPublicEmailError("");
    setPhoneError("");
    setWebsiteError("");
    setStateError("");
    setRoleError("");
    setConsentError("");
    setSuccessMsg("");
    setFormError("");

    const invalid = new Set();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!isLoggedIn) {
      if (!firstName.trim()) {
        setFirstNameError("First name is required.");
        invalid.add("firstName");
      }
      if (!lastName.trim()) {
        setLastNameError("Last name is required.");
        invalid.add("lastName");
      }
      if (!email.trim()) {
        setEmailErrorMsg("Email is required.");
        invalid.add("email");
      } else if (!emailRegex.test(email.trim())) {
        setEmailErrorMsg("Please enter a valid email address.");
        invalid.add("email");
      }
      if (!role) {
        setRoleError("Please select your professional role.");
        invalid.add("role");
      }
    }
    // Phone and State are required for every submitter — neither is derivable
    // from a logged-in account, so the guest-only block above cannot cover them.
    if (!phone.trim()) {
      setPhoneError("Phone number is required.");
      invalid.add("phone");
    } else if (!/[0-9]/.test(phone)) {
      setPhoneError("Please enter a valid phone number.");
      invalid.add("phone");
    }
    if (!stateVal.trim()) {
      setStateError("State is required.");
      invalid.add("state");
    }
    // The form opts out of native validation so every error goes through the
    // scroll-to-first-error path below; these two mirror the server's rules.
    if (!useVerificationEmail && publicEmail.trim() && !emailRegex.test(publicEmail.trim())) {
      setPublicEmailError("Please enter a valid public contact email address.");
      invalid.add("publicEmail");
    }
    const trimmedWebsite = website.trim();
    if (trimmedWebsite) {
      try {
        // Chrome's URL parser tolerates spaces that the server's rejects.
        if (/\s/.test(trimmedWebsite)) throw new Error("whitespace");
        new URL(/^https?:\/\//i.test(trimmedWebsite) ? trimmedWebsite : `https://${trimmedWebsite}`);
      } catch (err) {
        setWebsiteError("Please enter a valid website URL.");
        invalid.add("website");
      }
    }
    if (!publicList) {
      setConsentError("Please agree to the directory listing terms.");
      invalid.add("consent");
    }

    if (invalid.size) {
      setInvalidFocus({ field: VALIDATED_FIELD_ORDER.find((f) => invalid.has(f)) });
      return;
    }

    const trimmedPublicEmail = publicEmail.trim();
    const publishEmail = useVerificationEmail || !!trimmedPublicEmail;

    try {
      setSubmitting(true);
      setFormError("");
      setFormErrorCode("");
      const res = await submitDirectoryListing("directory", {
        firstName,
        lastName,
        email,
        publishEmail,
        publicEmail: useVerificationEmail ? "" : trimmedPublicEmail,
        phone,
        publishPhone,
        city,
        state: stateVal,
        firmName,
        yearsInPractice,
        role,
        practiceAreas,
        practiceAreaOther: otherArea,
        freeConsultation: freeConsultation === true,
        website,
        publicList,
      });

      setVerificationSent(!!res.emailVerificationSent);
      setSuccessMsg(res.message || "Your information has been saved successfully!");
    } catch (err) {
      setFormError(err.message || "Failed to submit directory listing.");
      setFormErrorCode(err.code || "");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  if (successMsg) {
    return (
      <div
        ref={successRef}
        style={{
          scrollMarginTop: "90px",
          border: `1.5px solid ${darkMode ? "#3b5bdb" : "#4750d6"}`,
          borderRadius: "16px",
          padding: "3rem 2rem",
          background: darkMode ? "#0f172a" : "#fff",
          boxShadow: darkMode ? "none" : "0 8px 24px rgba(71,80,214,0.08)",
          textAlign: "center",
        }}
      >
        <div
          style={{
            width: "64px",
            height: "64px",
            borderRadius: "50%",
            background: darkMode ? "rgba(34,197,94,0.18)" : "#dcfce7",
            color: "#16a34a",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 22px",
          }}
        >
          <Check size={30} strokeWidth={2.5} />
        </div>

        <h3
          style={{
            fontSize: "26px",
            fontWeight: 800,
            margin: "0 0 12px",
            color: darkMode ? "#f8fafc" : "#1a2456",
          }}
        >
          Your Listing Has Been Submitted
        </h3>
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "12px",
            textAlign: "left",
            background: darkMode ? "rgba(255,255,255,0.03)" : "#f8fafc",
            border: `1px solid ${darkMode ? "rgba(172,208,255,0.18)" : "#e3e6f0"}`,
            borderRadius: "12px",
            padding: "16px 18px",
            maxWidth: "520px",
            margin: "0 auto 40px",
          }}
        >
          <Mail
            size={20}
            style={{ flexShrink: 0, marginTop: "2px", color: darkMode ? "#94a3b8" : "#3d4570" }}
          />
          <div>
            {verificationSent && (
              <p
                style={{
                  fontSize: "14.5px",
                  fontWeight: 500,
                  lineHeight: 1.5,
                  margin: "0 0 8px",
                  color: darkMode ? "#94a3b8" : "#3d4570",
                }}
              >
                We've sent a verification link to your email — please allow a few
                minutes for it to arrive, and check your junk folder if you don't
                see it.
              </p>
            )}
            <p
              style={{
                fontSize: "14.5px",
                fontWeight: 500,
                lineHeight: 1.5,
                margin: 0,
                color: darkMode ? "#94a3b8" : "#3d4570",
              }}
            >
              {verificationSent
                ? "After you verify your email, your listing will be reviewed and manually approved before it appears in the Free Directory."
                : "Your listing will be reviewed and manually approved before it appears in the Free Directory."}
            </p>
          </div>
        </div>

        {/* The upsell is for free-listing submitters. An account already on a
            plan (or trialing / tester) has the featured placement and the
            AI tools this pitches, so it gets the confirmation alone. */}
        {!hasPremiumFeatureAccess(user) && (
        <div
          style={{
            background: darkMode ? "rgba(71,80,214,0.12)" : "#eef1fd",
            border: `1px solid ${darkMode ? "rgba(129,140,248,0.3)" : "#c7d2fe"}`,
            borderRadius: "16px",
            padding: "28px 30px",
            maxWidth: "520px",
            margin: "0 auto",
          }}
        >
          <h4
            style={{
              fontSize: "19px",
              fontWeight: 800,
              margin: "0 0 12px",
              color: darkMode ? "#f8fafc" : "#1a2456",
            }}
          >
            Feature Your Listing + Unlock LawFusion
          </h4>
          <div
            style={{
              background: darkMode ? "#0f172a" : "#fff",
              border: `1.5px solid ${darkMode ? "#3b5bdb" : "#4750d6"}`,
              borderRadius: "10px",
              padding: "14px 18px",
              margin: "0 0 22px",
            }}
          >
            <p
              style={{
                fontSize: "15px",
                fontWeight: 600,
                lineHeight: 1.5,
                margin: 0,
                color: darkMode ? "#e2e8f0" : "#1a2456",
              }}
            >
              Get featured directory placement, plus multiple AI perspectives to
              research and pressure-test your cases.
            </p>
          </div>
          <Link
            to="/pricing"
            style={{
              background: "#4750d6",
              color: "#fff",
              fontWeight: 700,
              fontSize: "15px",
              padding: "14px 28px",
              borderRadius: "10px",
              textDecoration: "none",
              display: "inline-block",
            }}
          >
            Try It Free
          </Link>
        </div>
        )}
      </div>
    );
  }

  const inputStyle = (hasError) => ({
    width: "100%",
    padding: "7px 10px",
    borderRadius: "8px",
    border: hasError
      ? "1.5px solid #ef4444"
      : `1px solid ${darkMode ? "rgba(172,208,255,0.25)" : "#acd0ff"}`,
    background: darkMode ? "#1e293b" : "#f8fbff",
    color: darkMode ? "#e2e8f0" : "#172033",
    fontSize: "14px",
    fontFamily: "inherit",
    outline: "none",
  });

  const labelStyle = {
    display: "block",
    fontSize: "13px",
    fontWeight: 700,
    color: darkMode ? "#93c5fd" : "#24458f",
    marginBottom: "6px",
  };

  return (
    <div
      style={{
        border: `1.5px solid ${darkMode ? "#3b5bdb" : "#4750d6"}`,
        borderRadius: "16px",
        padding: "1.75rem",
        background: darkMode ? "#0f172a" : "#fff",
        boxShadow: darkMode ? "none" : "0 8px 24px rgba(71,80,214,0.08)",
      }}
    >
      <h3 style={{ margin: "0 0 4px", fontSize: "20px", color: darkMode ? "#f8fafc" : "#1a2456" }}>
        Create Your Directory Listing
      </h3>
      <p style={{ margin: "0 0 22px", fontSize: "14px", color: darkMode ? "#94a3b8" : "#6b7280" }}>
        Free for attorneys and paralegals. Takes about a minute.
      </p>

      <form onSubmit={handleSubmit} noValidate>
        {isLoggedIn && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.6rem",
              padding: "0.6rem 0.85rem",
              marginBottom: "0.85rem",
              borderRadius: "8px",
              background: darkMode ? "rgba(59,130,246,0.1)" : "#eff6ff",
              border: `1px solid ${darkMode ? "rgba(96,165,250,0.25)" : "#bfdbfe"}`,
              fontSize: "13px",
              color: darkMode ? "#93c5fd" : "#1d4ed8",
              fontWeight: 500,
            }}
          >
            Applying as <strong>{user?.name || "your account"}</strong>
            {user?.email ? ` (${user.email})` : ""} — we'll use the name and email already on file.
          </div>
        )}

        {!isLoggedIn && (
          <>
            <div className="dform-grid-2" style={{ gap: "0.75rem 1rem", marginBottom: "0.75rem" }}>
              <div style={{ display: "grid", gap: "4px" }}>
                <label style={labelStyle}>
                  First Name <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <input
                  type="text"
                  ref={firstNameRef}
                  placeholder="First name"
                  value={firstName}
                  onChange={(e) => {
                    setFirstName(e.target.value);
                    if (firstNameError) setFirstNameError("");
                  }}
                  style={inputStyle(firstNameError)}
                />
                {firstNameError && (
                  <span style={{ fontSize: "12px", color: "#ef4444", fontWeight: 500 }}>{firstNameError}</span>
                )}
              </div>

              <div style={{ display: "grid", gap: "4px" }}>
                <label style={labelStyle}>
                  Last Name <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <input
                  type="text"
                  ref={lastNameRef}
                  placeholder="Last name"
                  value={lastName}
                  onChange={(e) => {
                    setLastName(e.target.value);
                    if (lastNameError) setLastNameError("");
                  }}
                  style={inputStyle(lastNameError)}
                />
                {lastNameError && (
                  <span style={{ fontSize: "12px", color: "#ef4444", fontWeight: 500 }}>{lastNameError}</span>
                )}
              </div>
            </div>

            <div style={{ marginBottom: "0.75rem" }}>
              <label style={labelStyle}>
                Verification Email <span style={{ color: "#ef4444" }}>*</span>{" "}
                <span style={{ fontWeight: 500, color: darkMode ? "#64748b" : "#8A94A6", fontSize: "12px" }}>
                  (private by default)
                </span>
              </label>
              <input
                type="email"
                ref={emailRef}
                placeholder="you@yourfirm.com"
                autoComplete="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (emailErrorMsg) setEmailErrorMsg("");
                }}
                style={inputStyle(emailErrorMsg)}
              />
              {emailErrorMsg ? (
                <p style={{ margin: "4px 0 0", fontSize: "12px", color: "#ef4444", fontWeight: 500 }}>{emailErrorMsg}</p>
              ) : (
                <p style={{ margin: "4px 0 0", fontSize: "12px", color: darkMode ? "#64748b" : "#8A94A6", lineHeight: "1.4" }}>
                  Used to verify you control this listing. Never shown publicly unless you opt in below.
                </p>
              )}
            </div>
          </>
        )}

        {/* Public contact email */}
        <div style={{ marginBottom: "0.5rem" }}>
          <label style={labelStyle}>
            Public Contact Email{" "}
            <span style={{ fontWeight: 500, color: darkMode ? "#64748b" : "#8A94A6", fontSize: "12px" }}>
              (optional)
            </span>
          </label>
          <input
            ref={publicEmailRef}
            type="email"
            placeholder="Shown on your listing if provided"
            value={publicEmail}
            disabled={useVerificationEmail}
            onChange={(e) => {
              setPublicEmail(e.target.value);
              if (publicEmailError) setPublicEmailError("");
            }}
            style={{ ...inputStyle(publicEmailError), opacity: useVerificationEmail ? 0.6 : 1 }}
          />
          {publicEmailError && (
            <p style={{ margin: "4px 0 0", fontSize: "12px", color: "#ef4444", fontWeight: 500 }}>{publicEmailError}</p>
          )}
        </div>
        <label style={{ display: "flex", alignItems: "flex-start", gap: "9px", padding: "9px 11px", marginBottom: "0.85rem", background: darkMode ? "rgba(255,255,255,0.03)" : "#fff", border: `1px solid ${darkMode ? "rgba(172,208,255,0.18)" : "#dbe6f5"}`, borderRadius: "8px", cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={useVerificationEmail}
            onChange={(e) => handleUseVerificationEmailChange(e.target.checked)}
            style={{ marginTop: "2px", width: "15px", height: "15px", flexShrink: 0, accentColor: "#2f62e9" }}
          />
          <span style={{ fontSize: "13px", color: darkMode ? "#94a3b8" : "#3b4d68", lineHeight: "1.4", fontWeight: 500 }}>
            Use my {isLoggedIn ? "account" : "verification"} email as my public directory contact email.
          </span>
        </label>

        {/* Phone number — required, private unless the submitter opts in */}
        <div style={{ marginBottom: "0.5rem" }}>
          <label style={labelStyle}>
            Phone Number <span style={{ color: "#ef4444" }}>*</span>{" "}
            <span style={{ fontWeight: 500, color: darkMode ? "#64748b" : "#8A94A6", fontSize: "12px" }}>
              (private by default)
            </span>
          </label>
          <input
            type="tel"
            ref={phoneRef}
            placeholder="(555) 123-4567"
            autoComplete="tel"
            value={phone}
            onChange={(e) => {
              setPhone((prev) => nextPhoneValue(prev, e.target.value));
              if (phoneError) setPhoneError("");
            }}
            style={inputStyle(phoneError)}
          />
          {phoneError ? (
            <p style={{ margin: "4px 0 0", fontSize: "12px", color: "#ef4444", fontWeight: 500 }}>{phoneError}</p>
          ) : (
            <p style={{ margin: "4px 0 0", fontSize: "12px", color: darkMode ? "#64748b" : "#8A94A6", lineHeight: "1.4" }}>
              May be used to contact you about your listing. Not shown publicly unless you opt in below.
            </p>
          )}
        </div>
        <label style={{ display: "flex", alignItems: "flex-start", gap: "9px", padding: "9px 11px", marginBottom: "0.85rem", background: darkMode ? "rgba(255,255,255,0.03)" : "#fff", border: `1px solid ${darkMode ? "rgba(172,208,255,0.18)" : "#dbe6f5"}`, borderRadius: "8px", cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={publishPhone}
            onChange={(e) => setPublishPhone(e.target.checked)}
            style={{ marginTop: "2px", width: "15px", height: "15px", flexShrink: 0, accentColor: "#2f62e9" }}
          />
          <span style={{ fontSize: "13px", color: darkMode ? "#94a3b8" : "#3b4d68", lineHeight: "1.4", fontWeight: 500 }}>
            Show my phone number on my directory listing.
          </span>
        </label>

        {/* City / State */}
        <div className="dform-grid-2" style={{ gap: "0.75rem 1rem", marginBottom: "0.75rem" }}>
          <div style={{ display: "grid", gap: "4px" }}>
            <label style={labelStyle}>
              City <span style={{ fontWeight: 500, color: darkMode ? "#64748b" : "#8A94A6", fontSize: "12px" }}>(optional)</span>
            </label>
            <input type="text" placeholder="City" value={city} onChange={(e) => setCity(e.target.value)} style={inputStyle(false)} />
          </div>
          <div style={{ display: "grid", gap: "4px" }}>
            <label style={labelStyle}>
              State <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <input
              type="text"
              ref={stateRef}
              placeholder="State"
              value={stateVal}
              onChange={(e) => {
                setStateVal(e.target.value);
                if (stateError) setStateError("");
              }}
              style={inputStyle(stateError)}
            />
            {stateError && (
              <span style={{ fontSize: "12px", color: "#ef4444", fontWeight: 500 }}>{stateError}</span>
            )}
          </div>
        </div>

        {/* Firm / Years in Practice */}
        <div className="dform-grid-2" style={{ gap: "0.75rem 1rem", marginBottom: "0.75rem" }}>
          <div style={{ display: "grid", gap: "4px" }}>
            <label style={labelStyle}>
              Firm / Organization <span style={{ fontWeight: 500, color: darkMode ? "#64748b" : "#8A94A6", fontSize: "12px" }}>(optional)</span>
            </label>
            <input type="text" placeholder="Firm name" value={firmName} onChange={(e) => setFirmName(e.target.value)} style={inputStyle(false)} />
          </div>
          <div style={{ display: "grid", gap: "4px" }}>
            <label style={labelStyle}>
              Years in Practice <span style={{ fontWeight: 500, color: darkMode ? "#64748b" : "#8A94A6", fontSize: "12px" }}>(optional)</span>
            </label>
            <input
              type="text"
              placeholder="e.g., 5, 10, 15+"
              value={yearsInPractice}
              onChange={(e) => setYearsInPractice(e.target.value)}
              style={inputStyle(false)}
            />
          </div>
        </div>

        {/* Professional Website */}
        <div style={{ marginBottom: "0.75rem" }}>
          <label style={labelStyle}>
            Professional Website <span style={{ fontWeight: 500, color: darkMode ? "#64748b" : "#8A94A6", fontSize: "12px" }}>(optional)</span>
          </label>
          <input
            ref={websiteRef}
            type="url"
            placeholder="https://"
            value={website}
            onChange={(e) => {
              setWebsite(e.target.value);
              if (websiteError) setWebsiteError("");
            }}
            style={inputStyle(websiteError)}
          />
          {websiteError && (
            <p style={{ margin: "4px 0 0", fontSize: "12px", color: "#ef4444", fontWeight: 500 }}>{websiteError}</p>
          )}
        </div>

        {/* Professional Role */}
        {!isLoggedIn && (
          <div style={{ marginBottom: "0.75rem" }}>
            <label style={labelStyle}>
              Professional Role <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <div ref={roleRef} data-pulse-children="true" style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
              {["Attorney", "Paralegal"].map((r) => (
                <label
                  key={r}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "7px",
                    border: roleError
                      ? "1.5px solid #ef4444"
                      : `1px solid ${role === r ? "#2f62e9" : darkMode ? "rgba(172,208,255,0.25)" : "#cfd9e7"}`,
                    borderRadius: "8px",
                    padding: "7px 13px",
                    background: role === r ? (darkMode ? "rgba(47,98,233,0.2)" : "#dbe8ff") : darkMode ? "#1e293b" : "#fff",
                    color: darkMode ? "#e2e8f0" : "#27364f",
                    fontWeight: 600,
                    fontSize: "13.5px",
                    cursor: "pointer",
                    transition: "all 0.15s",
                  }}
                >
                  <input
                    type="radio"
                    name="directory-page-role"
                    value={r}
                    checked={role === r}
                    onChange={() => {
                      setRole(r);
                      if (roleError) setRoleError("");
                    }}
                    style={{ accentColor: "#2f62e9" }}
                  />
                  {r}
                </label>
              ))}
            </div>
            {roleError && (
              <span style={{ display: "block", marginTop: "4px", fontSize: "12px", color: "#ef4444", fontWeight: 500 }}>
                {roleError}
              </span>
            )}
          </div>
        )}

        {/* Free consultation */}
        <div style={{ marginBottom: "0.75rem" }}>
          <label style={labelStyle}>Do you offer a free consultation?</label>
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            {[
              { label: "Yes", value: true },
              { label: "No", value: false },
            ].map(({ label, value }) => (
              <label
                key={label}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "7px",
                  border: `1px solid ${freeConsultation === value ? "#2f62e9" : darkMode ? "rgba(172,208,255,0.25)" : "#cfd9e7"}`,
                  borderRadius: "8px",
                  padding: "7px 13px",
                  background:
                    freeConsultation === value
                      ? darkMode
                        ? "rgba(47,98,233,0.2)"
                        : "#dbe8ff"
                      : darkMode
                        ? "#1e293b"
                        : "#fff",
                  color: darkMode ? "#e2e8f0" : "#27364f",
                  fontWeight: 600,
                  fontSize: "13.5px",
                  cursor: "pointer",
                  transition: "all 0.15s",
                }}
              >
                <input
                  type="radio"
                  name="directory-page-free-consultation"
                  checked={freeConsultation === value}
                  onChange={() => setFreeConsultation(value)}
                  style={{ accentColor: "#2f62e9" }}
                />
                {label}
              </label>
            ))}
          </div>
        </div>

        {/* Practice Areas */}
        <div style={{ marginBottom: "0.75rem" }}>
          <label style={labelStyle}>
            Practice Areas{" "}
            <span style={{ fontWeight: 500, color: darkMode ? "#64748b" : "#8A94A6", fontSize: "12px" }}>
              {practiceAreas.size === 0 ? "(select up to 3)" : `(${practiceAreas.size} of ${PA_MAX} selected)`}
            </span>
          </label>
          <div className="dform-grid-2" style={{ gap: "7px" }}>
            {PA_OPTIONS.map((area) => {
              const checked = practiceAreas.has(area);
              const disabled = !checked && practiceAreas.size >= PA_MAX;
              return (
                <label
                  key={area}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    border: `1px solid ${checked ? "#2f62e9" : darkMode ? "rgba(172,208,255,0.25)" : "#cfd9e7"}`,
                    borderRadius: "8px",
                    padding: "7px 10px",
                    background: checked ? (darkMode ? "rgba(47,98,233,0.18)" : "#dbe8ff") : darkMode ? "#1e293b" : "#fff",
                    color: checked ? (darkMode ? "#93c5fd" : "#2347b2") : darkMode ? "#e2e8f0" : "#27364f",
                    fontWeight: 600,
                    fontSize: "13px",
                    cursor: disabled ? "not-allowed" : "pointer",
                    opacity: disabled ? 0.45 : 1,
                    lineHeight: "1.25",
                    transition: "all 0.12s",
                  }}
                >
                  <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => handlePAChange(area, e.target.checked)} style={{ flexShrink: 0, width: "15px", height: "15px", margin: 0, accentColor: "#2f62e9" }} />
                  {/* Ticking "Other" reveals a free-text field, so the label
                      says so rather than leaving the reader to discover it. */}
                  {area === "Other" ? "Other (type your practice area)" : area}
                </label>
              );
            })}
          </div>
          {practiceAreas.has("Other") && (
            <input
              type="text"
              placeholder="Enter your practice area"
              value={otherArea}
              /* Capped to keep a custom entry the same sort of length as the
                 built-in labels, the longest of which is 34 characters —
                 anything longer breaks the listing cards it sits in. */
              maxLength={PA_OTHER_MAX}
              onChange={(e) => setOtherArea(e.target.value.slice(0, PA_OTHER_MAX))}
              style={{ ...inputStyle(false), marginTop: "8px" }}
            />
          )}
        </div>

        {/* Public listing consent */}
        <div style={{ marginBottom: "1rem" }}>
          <label
            ref={consentRef}
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "10px",
              padding: "10px 12px",
              background: darkMode ? "rgba(255,255,255,0.03)" : "#fff",
              border: consentError ? "1.5px solid #ef4444" : `1px solid ${darkMode ? "rgba(172,208,255,0.18)" : "#dbe6f5"}`,
              borderRadius: "8px",
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={publicList}
              onChange={(e) => {
                setPublicList(e.target.checked);
                if (consentError) setConsentError("");
              }}
              style={{ marginTop: "2px", width: "16px", height: "16px", flexShrink: 0, accentColor: "#2f62e9" }}
            />
            <span style={{ fontSize: "13.5px", color: darkMode ? "#94a3b8" : "#3b4d68", lineHeight: "1.4", fontWeight: 500 }}>
              I agree that LawFusion may publish my name, professional role, practice areas, and any website or email I have chosen to make public in its legal-professional directory. I may request removal at any time. <span style={{ color: "#ef4444" }}>*</span>
            </span>
          </label>
          {consentError && (
            <p style={{ margin: "4px 0 0", fontSize: "12px", color: "#ef4444", fontWeight: 500 }}>{consentError}</p>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "14px" }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: darkMode ? "#94a3b8" : "#6b7280",
              fontWeight: 700,
              fontSize: "15px",
              cursor: "pointer",
              padding: "12px 8px",
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            style={{
              background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
              color: "#fff",
              border: "none",
              borderRadius: "9px",
              fontWeight: 700,
              fontSize: "15px",
              padding: "13px 26px",
              cursor: submitting ? "not-allowed" : "pointer",
              opacity: submitting ? 0.7 : 1,
            }}
          >
            {submitting ? "Submitting..." : "Submit My Listing"}
          </button>
        </div>


        {formError && (
          <div
            style={{
              padding: "0.75rem 1rem",
              marginTop: "0.75rem",
              borderRadius: "8px",
              background: darkMode ? "rgba(239, 68, 68, 0.15)" : "#fee2e2",
              border: `1px solid ${darkMode ? "rgba(239, 68, 68, 0.3)" : "#fca5a5"}`,
              color: darkMode ? "#f87171" : "#991b1b",
              fontSize: "13px",
              fontWeight: 600,
            }}
          >
            {formError}
            {formErrorCode === "DUPLICATE_APPROVED" && (
              <>
                {" "}
                <Link
                  to="/login?area=directory"
                  style={{ color: "inherit", textDecoration: "underline" }}
                >
                  Log in to the Directory
                </Link>
                .
              </>
            )}
          </div>
        )}
      </form>
    </div>
  );
}
